/**
 * Extension-to-MIME mapping for common document types.
 *
 * This is intentionally a superset of FILE_CONSTANTS.ACCEPTED_MIME_TYPES —
 * it maps every extension we can reliably identify, even if the MIME isn't
 * accepted for upload (e.g. CSV). Acceptance filtering happens downstream
 * via isFileTypeAccepted() checking against the accepted MIME list.
 */
const EXTENSION_MIME_MAP: Record<string, string> = {
  ".pdf": "application/pdf",
  ".doc": "application/msword",
  ".docx": "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  ".xls": "application/vnd.ms-excel",
  ".xlsx": "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  ".csv": "text/csv",
  // Claimant attachment allow-list (#6506): correspondence, EOBs, carrier
  // letters, signed forms. Extension-based so acceptance still works when the
  // browser reports an empty file.type (common for .msg / .eml).
  ".txt": "text/plain",
  ".png": "image/png",
  ".jpg": "image/jpeg",
  ".jpeg": "image/jpeg",
  ".gif": "image/gif",
  ".tif": "image/tiff",
  ".tiff": "image/tiff",
  ".eml": "message/rfc822",
  ".msg": "application/vnd.ms-outlook",
};

/**
 * Returns the MIME type for a filename based on its extension.
 * Case-insensitive. Returns null if the extension is not recognized.
 */
export const getMimeTypeFromExtension = (filename: string): string | null => {
  const lastDot = filename.lastIndexOf(".");
  if (lastDot === -1) return null;
  const ext = filename.slice(lastDot).toLowerCase();
  return EXTENSION_MIME_MAP[ext] ?? null;
};

/**
 * Returns the MIME type for a File object.
 * Uses the browser-supplied `file.type` when present, otherwise falls back
 * to extension-based detection.
 *
 * Exception: `.csv` extension always wins. Windows browsers report
 * `application/vnd.ms-excel` for CSV files, which would otherwise route
 * the file to the worker's Excel parser.
 */
export const detectFileType = (file: File): string | null => {
  if (file.name.toLowerCase().endsWith(".csv")) return "text/csv";
  if (file.type) return file.type;
  return getMimeTypeFromExtension(file.name);
};

/**
 * Checks whether a file's type is in the accepted MIME list.
 * Routes through detectFileType first so the `.csv`-extension override is
 * honored here too (keeps the two helpers aligned on Windows-quirk inputs).
 * Falls back to extension-derived MIME when the browser-reported type isn't
 * accepted but the extension is — e.g. a `.pdf` reported as `text/plain`.
 */
export const isFileTypeAccepted = (file: File, acceptedMimes: string[]): boolean => {
  const detected = detectFileType(file);
  if (detected !== null && acceptedMimes.includes(detected)) return true;
  const fallback = getMimeTypeFromExtension(file.name);
  return fallback !== null && acceptedMimes.includes(fallback);
};
